/**
 * Entregar el sitio a otra cuenta.
 *
 * Tuvo su recuadro al pie de «General», pegado al borrado, porque las dos son
 * la misma clase de decisión: dejar de tener este proyecto. Salió de ahí al
 * crecer — entregar no es una variante de destruir, y lo que hace falta saber
 * antes de hacerlo no cabe en tres renglones bajo otra pantalla.
 *
 * Siempre por invitación, nunca un empujón: quien recibe se lleva una factura,
 * un dominio y datos personales de terceros. Nada de eso puede aterrizar en una
 * cuenta ajena sin que su dueño lo acepte.
 *
 * La pantalla contesta tres preguntas, en este orden:
 *
 *   1. Qué pasa si acepta. Antes esto sólo se le contaba a quien RECIBE, así
 *      que quien entregaba firmaba a ciegas la desconexión de su cobrador y el
 *      vaciado de su equipo.
 *   2. A quién se lo mando (o la invitación viva, con reenviar y cancelar).
 *   3. Qué ha pasado con lo que ya mandé. Ver `listSentTransfers`: se consulta
 *      por quien envió y no por proyecto, que es lo único que sobrevive al
 *      cambio de dueño.
 */
import { useEffect, useState } from 'react'
import { longDate } from '../../../lib/dates'
import {
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconRefresh,
  IconSend,
  IconUsers,
  IconWallet,
  IconX,
} from '@tabler/icons-react'
import { cancelProjectTransfer, getProjectTransfer, inviteProjectTransfer, listSentTransfers, resendProjectTransfer, type ProjectTransfer, type SentTransfer } from '../../../lib/api'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { useToast } from '../../Toast'
import { useI18n, translateServerError } from '../../../lib/i18n'

/** El tono de cada desenlace, que es lo que decide el color de su pastilla. */
const STATUS_TONE: Record<SentTransfer['status'], 'idle' | 'ok' | 'warn'> = {
  pending: 'idle',
  accepted: 'ok',
  rejected: 'warn',
  cancelled: 'idle',
  expired: 'warn',
}

export function TransferProjectCard({ projectId, token, projectName }: {
  projectId: string
  token: string
  projectName: string
}) {
  const { t } = useI18n()
  const [pending, setPending] = useState<ProjectTransfer | null>(null)
  const [sent, setSent] = useState<SentTransfer[] | null>(null)
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const { showToast } = useToast()

  const loadSent = () => {
    listSentTransfers(token)
      .then(setSent)
      /* El historial es un extra: si falla, la pantalla sigue sirviendo para lo
         que se viene a hacer, que es mandar una invitación. */
      .catch(() => setSent([]))
  }

  useEffect(() => {
    let alive = true
    getProjectTransfer(token, projectId)
      .then((row) => { if (alive) setPending(row) })
      /* Sin invitación viva es exactamente lo mismo que no poder consultarlo:
         en los dos casos lo que se enseña es el formulario para mandar una. */
      .catch(() => {})
    listSentTransfers(token)
      .then((rows) => { if (alive) setSent(rows) })
      .catch(() => { if (alive) setSent([]) })
    return () => { alive = false }
  }, [projectId, token])

  const invite = async () => {
    const address = email.trim().toLowerCase()
    if (!address) return
    setBusy(true)
    setError('')
    try {
      const created = await inviteProjectTransfer(token, projectId, address)
      setPending({
        id: created.id,
        toEmail: created.email,
        status: 'pending',
        expiresAt: created.expiresAt,
        createdAt: new Date().toISOString(),
      })
      setEmail('')
      showToast(t('transferCard.sentTo', { email: address }))
      loadSent()
    } catch (cause) {
      setError(translateServerError(cause, t('error.unexpected')))
    } finally {
      setBusy(false)
    }
  }

  const resend = async () => {
    if (!pending) return
    setBusy(true)
    setError('')
    try {
      const again = await resendProjectTransfer(token, projectId, pending.id)
      /* El plazo vuelve a empezar en el servidor, así que la fecha de la
         pantalla tiene que venir de su respuesta y no quedarse en la vieja. */
      setPending({ ...pending, expiresAt: again.expiresAt })
      showToast(t('transferCard.resent', { email: pending.toEmail }))
    } catch (cause) {
      setError(translateServerError(cause, t('error.unexpected')))
    } finally {
      setBusy(false)
    }
  }

  const cancel = async () => {
    if (!pending) return
    setBusy(true)
    setError('')
    try {
      await cancelProjectTransfer(token, projectId, pending.id)
      setPending(null)
      showToast(t('transferCard.cancelled'))
      loadSent()
    } catch (cause) {
      setError(translateServerError(cause, t('error.unexpected')))
    } finally {
      setBusy(false)
    }
  }

  /* Lo que se pierde al entregar, con su icono. Se lee ANTES del formulario a
     propósito: es la parte que no se puede deshacer, y ponerla debajo del botón
     la convierte en letra pequeña. */
  const consequences = [
    { key: 'owner', icon: <IconSend size={15} aria-hidden="true" /> },
    { key: 'team', icon: <IconUsers size={15} aria-hidden="true" /> },
    { key: 'stripe', icon: <IconWallet size={15} aria-hidden="true" /> },
    { key: 'data', icon: <IconCheck size={15} aria-hidden="true" /> },
    { key: 'plan', icon: <IconAlertTriangle size={15} aria-hidden="true" /> },
  ] as const

  /* La letra pequeña del envío. Se declara aquí porque cambia de sitio y no de
     texto: dentro de la caja del formulario, donde es la advertencia de lo que
     va a pasar al pulsar; y suelta bajo la invitación viva, donde lo que dice es
     que todavía se puede echar atrás. */
  const hint = <p className="dash-transfer-hint">{t('transferCard.hint', { project: projectName })}</p>

  return (
    /* `dash-settings-view` y no el envoltorio de las listas: de él cuelgan las
       reglas que visten estas tarjetas —la cabecera como fila con su icono de
       34px, el relleno de 28 y la escala del título—. Sin él la cabecera caía
       apilada y en letra pequeña, que era lo que se veía. Es el mismo par que
       usan Datos del negocio y Región, que son pantallas de la misma clase. */
    <div className="dash-dashboard-view dash-settings-view dash-transfer-view dash-loaded-content-in">
      <DashboardPageHeader
        title={t('section.transfer')}
        subtitle={projectName}
      />

      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconSend size={18} /></span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('transferCard.title')}</h3>
            <p>{t('transferCard.body')}</p>
          </div>
        </div>

        {/* 1. Las consecuencias. */}
        <div className="dash-transfer-consequences">
          <strong>{t('transferCard.consequences.title')}</strong>
          <ul>
            {consequences.map((row) => (
              <li key={row.key}>
                {row.icon}
                <span>{t(`transferCard.consequences.${row.key}`)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* 2. La invitación viva, o el formulario. */}
        {pending ? (
          /* Una sola invitación viva por proyecto. Con varias, aceptar la
             primera dejaría al resto apuntando a un sitio que ya cambió de
             dueño. */
          <div className="dash-transfer-pending-card">
            <span className="dash-widget-glass-icon dash-transfer-pending-icon" aria-hidden="true"><IconClock size={17} /></span>
            <div className="dash-transfer-pending-copy">
              <div>
                <strong>{t('transferCard.pending')}</strong>
                <span className="dash-connect-status">{t('transferCard.status.pending')}</span>
              </div>
              <small>{t('transferCard.pendingMeta', { email: pending.toEmail, date: longDate(pending.expiresAt) })}</small>
            </div>
            <span className="dash-transfer-pending-actions">
              {/* Reenviar antes que cancelar: quien llega aquí porque el correo
                  no apareció viene a insistir, no a rendirse. */}
              <button className="dash-secondary-btn" type="button" disabled={busy} onClick={() => void resend()}>
                <IconRefresh size={14} /> {t('transferCard.resend')}
              </button>
              <button className="dash-secondary-btn" type="button" disabled={busy} onClick={() => void cancel()}>
                <IconX size={14} /> {t('transferCard.cancelInvite')}
              </button>
            </span>
          </div>
        ) : (
          /* En caja, como las consecuencias de arriba: es un bloque de la misma
             clase y el único de la pantalla donde se hace algo. */
          <div className="dash-transfer-invite-box">
            <div className="dash-transfer-invite">
              <label className="dash-field">
                <span>{t('transferCard.emailLabel')}</span>
                <input
                  type="email"
                  value={email}
                  placeholder={t('transferCard.emailPlaceholder')}
                  autoComplete="off"
                  onChange={(event) => { setEmail(event.target.value); setError('') }}
                  onKeyDown={(event) => { if (event.key === 'Enter' && email.trim()) void invite() }}
                />
              </label>
              <button className="dash-primary-btn" type="button" disabled={busy || !email.trim()} onClick={() => void invite()}>
                <IconSend size={14} /> {t(busy ? 'transferCard.sending' : 'transferCard.send')}
              </button>
            </div>
            {hint}
          </div>
        )}

        {/* El fallo, fuera de la caja: también lo dejan reenviar y cancelar, que
            viven en la otra rama. */}
        {error ? <p className="dash-transfer-error">{error}</p> : null}

        {pending ? hint : null}
      </section>

      {/* 3. Qué pasó con lo que ya mandé. */}
      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconClock size={18} /></span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('transferCard.history')}</h3>
          </div>
        </div>

        {sent !== null && sent.length === 0 ? (
          /* El mismo vacío que el resto del panel —icono, frase y matiz— y no un
             renglón gris suelto: una lista vacía y una lista que no cargó se
             veían igual. */
          /* Sin `--inset`: eso le quita el radio y la sombra para que llene una
             caja de canto a canto, y aquí va DENTRO de una tarjeta con 28px de
             relleno, donde quedaría como una franja plana en vez de un hueco. */
          <div className="dash-placeholder">
            <IconSend size={34} aria-hidden="true" />
            <strong>{t('transferCard.historyEmpty')}</strong>
            <span>{t('transferCard.historyEmptyBody')}</span>
          </div>
        ) : (
          <ul className="dash-transfer-history">
            {(sent ?? []).map((row) => (
              <li key={row.id} className="dash-transfer-history-row">
                <span className="dash-widget-glass-icon dash-transfer-history-icon" aria-hidden="true">
                  {row.status === 'accepted' ? <IconCheck size={16} /> : <IconSend size={16} />}
                </span>
                <span className="dash-transfer-history-copy">
                  <strong>{row.projectName}</strong>
                  <small>
                    {row.toName
                      ? t('transferCard.acceptedBy', { name: row.toName })
                      : row.toEmail}
                    {' · '}
                    {row.resolvedAt
                      ? t('transferCard.resolvedOn', { date: longDate(row.resolvedAt) })
                      : t('transferCard.sentOn', { date: longDate(row.createdAt) })}
                  </small>
                  {/* Aceptado no es terminado, y ese hueco es el que le importa a
                      quien entregó: mientras dure, el sitio no cobra. */}
                  {row.status === 'accepted' ? (
                    <small className={row.handoverDoneAt ? 'is-ok' : 'is-warn'}>
                      {row.handoverDoneAt
                        ? t('transferCard.handoverDone')
                        : t('transferCard.handoverPending')}
                    </small>
                  ) : null}
                </span>
                <span className={`dash-connect-status is-${STATUS_TONE[row.status]}`}>
                  {t(`transferCard.status.${row.status}`)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
